import { SampleCategoryEnum } from "../enums/sampleCategory.enum";
import { SampleTypeEnum } from "../enums/sampleType.enum";
import { Sample } from "../models/sample.model";
import { ILookup } from "../shared/form/lookup.model";

export type SampleCondition = (sample: Sample) => boolean;

/**
 * Property carried by lookup options that have a "linked to my site" tag.
 * When truthy the entry is linked to the current user's site.
 */
export const LINKED_TO_MY_SITE_FIELD = 'addedToMySite';

/**
 * Default end time for a new sample's "End Time" field: noon (canonical 24h "HH:mm"),
 * which renders as 12:00 PM in 12-hour locales.
 */
export const DEFAULT_SAMPLE_END_TIME = '12:00';

/**
 * Sort blank-sample dropdown options in descending order by blank sample number
 * (the lookup `name`). Uses numeric-aware comparison so e.g. "Blank-10" sorts
 * above "Blank-2". Does not mutate the input array.
 */
export const sortBlankSamplesDescending = (options: ILookup[]): ILookup[] =>
    [...(options ?? [])].sort((a, b) =>
        (b?.name ?? '').localeCompare(a?.name ?? '', undefined, { numeric: true, sensitivity: 'base' }));

/** True when the given field renders a "linked to my site" badge. */
export const isLinkedToMySiteField = (badgeFields: string[] | undefined): boolean =>
    !!badgeFields?.includes(LINKED_TO_MY_SITE_FIELD);

/**
 * For fields that carry a "linked to my site" tag, keep only the options that are
 * actually linked to the current site. Fields without the badge are returned as-is.
 * Does not mutate the input array.
 */
export const filterLinkedToMySiteOptions = <T extends ILookup>(options: T[], badgeFields: string[] | undefined): T[] => {
    if (!isLinkedToMySiteField(badgeFields)) return options ?? [];
    return (options ?? []).filter(o => !!(o as Record<string, unknown>)[LINKED_TO_MY_SITE_FIELD]);
};

/** True when the two dates fall on different calendar days (local time). */
export const isDifferentCalendarDay = (a: Date, b: Date): boolean =>
    a.getFullYear() !== b.getFullYear() ||
    a.getMonth() !== b.getMonth() ||
    a.getDate() !== b.getDate();

export type SampleTypeCategoryPair = [SampleTypeEnum, SampleCategoryEnum];

export const SampleTypeCategoryCombos = {
    airPersonal: [SampleTypeEnum.Air, SampleCategoryEnum.Personal] as SampleTypeCategoryPair,
    airArea: [SampleTypeEnum.Air, SampleCategoryEnum.Area] as SampleTypeCategoryPair,
    airBlank: [SampleTypeEnum.Air, SampleCategoryEnum.Blank] as SampleTypeCategoryPair,
    noisePersonal: [SampleTypeEnum.Noise, SampleCategoryEnum.Personal] as SampleTypeCategoryPair,
    noiseArea: [SampleTypeEnum.Noise, SampleCategoryEnum.Area] as SampleTypeCategoryPair,
    wipeArea: [SampleTypeEnum.Wipe, SampleCategoryEnum.Area] as SampleTypeCategoryPair,
    wipeBlank: [SampleTypeEnum.Wipe, SampleCategoryEnum.Blank] as SampleTypeCategoryPair,
    wipePersonal: [SampleTypeEnum.Wipe, SampleCategoryEnum.Personal] as SampleTypeCategoryPair,
    // Biological / Other are Personal-only sample types (DASH-2621). They mirror the
    // Wipe/Personal field-visibility pattern so the sample dialog stays coherent; any other
    // (type, category) combo has no pair and therefore gates fields defensively (hidden, not broken).
    biologicalPersonal: [SampleTypeEnum.Biological, SampleCategoryEnum.Personal] as SampleTypeCategoryPair,
    psychologicalPersonal: [SampleTypeEnum.Other, SampleCategoryEnum.Personal] as SampleTypeCategoryPair
};

export const commonIsVisible = (sample: Sample) => { const { sampleType, sampleCategory, organizationLevelId } = sample; return sampleType != null && sampleCategory != null && organizationLevelId != null; };

export const sampleMatches = (sample: Sample, type: SampleTypeEnum, category: SampleCategoryEnum) =>
    sample.sampleType === type && sample.sampleCategory === category;

export const isVisibleExcept = (excluded: [SampleTypeEnum, SampleCategoryEnum][]): SampleCondition => {
    return (sample) => commonIsVisible(sample) && !excluded.some(([type, category]) => sampleMatches(sample, type, category));
};

export const isVisibleOnly = (allowed: [SampleTypeEnum, SampleCategoryEnum][]): SampleCondition => {
    return (sample) => commonIsVisible(sample) && allowed.some(([type, category]) => sampleMatches(sample, type, category));
};